import { Box, Stack } from '@chronosphereio/chrono-ui';
import { EventPayload, TimeRangeInputValue } from '@chronosphereio/core';
import { useMemo } from 'react';
import { VariableDefinition } from '@/core';
import { AppMetadataProvider } from '../../../plugin-system';
import { useSavedViews, useTemplateVariableDefinitions } from '../../context';
import { OnUpdateDefaultValues, OnUpdateSavedViews, SavedViewSelector } from './SavedViewSelector';
import { TemplateVariable } from './TemplateVariable';

type TemplateVariableListProps = {
  /**
   * If false, the variables will be wrapped in a Stack component.
   * @default false
   */
  disableContainer?: boolean;
  /**
   * If true, the horizontal padding between variables will be removed. Can be useful when
   * TemplateVariableList is wrapped in a Stack and you do not want extra spacing between the variables.
   * @default false
   */
  noPadding?: boolean;

  onUpdateSavedViews?: OnUpdateSavedViews;
  showSavedViews?: boolean;
  onUpdateDefaultValues?: OnUpdateDefaultValues;

  /**
   * Default time range is passed on to SavedViews for displaying the default time range value
   */
  defaultTimeRange?: TimeRangeInputValue;

  /**
   * Analytics payload that will be passed along on any analytics events
   */
  analyticsPayload?: EventPayload;

  /**
   * Callback fired when a user wants to edit a variable definition.
   * When provided, an "Edit variable" button appears in each variable's combobox footer.
   */
  onEditVariable?: (name: string) => void;
};

export function TemplateVariableList(props: TemplateVariableListProps) {
  const variableDefinitions: VariableDefinition[] = useTemplateVariableDefinitions();
  const savedViews = useSavedViews();

  const { disableContainer = false, noPadding = false } = props;

  const VariableList = useMemo(() => {
    return (
      <>
        {props.showSavedViews && (
          <Box data-testid="variable-view-selector" pr={noPadding ? 0 : 1} py={noPadding ? 0 : 0.5}>
            <SavedViewSelector
              savedViews={savedViews}
              onUpdateSavedViews={props.onUpdateSavedViews}
              onUpdateDefaultValues={props.onUpdateDefaultValues}
              defaultTimeRange={props.defaultTimeRange}
              analyticsPayload={props.analyticsPayload}
            />
          </Box>
        )}

        {variableDefinitions.map((v) => (
          <Box
            key={v.spec.name}
            display={v.spec.display?.hidden ? 'none' : undefined}
            data-testid="template-variable"
            pr={noPadding ? 0 : 1}
            py={noPadding ? 0 : 0.5}
          >
            <AppMetadataProvider
              metadata={{
                variableName: v.spec.name,
              }}
            >
              <TemplateVariable key={v.spec.name} name={v.spec.name} onEditVariable={props.onEditVariable} />
            </AppMetadataProvider>
          </Box>
        ))}
      </>
    );
  }, [
    noPadding,
    variableDefinitions,
    savedViews,
    props.onUpdateSavedViews,
    props.showSavedViews,
    props.onUpdateDefaultValues,
    props.defaultTimeRange,
    props.analyticsPayload,
    props.onEditVariable,
  ]);

  if (disableContainer) {
    return <>{VariableList}</>;
  } else {
    return (
      <Stack direction="row" alignItems="center" flexWrap="wrap">
        {VariableList}
      </Stack>
    );
  }
}
